import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Briefcase, Edit3 } from "lucide-react";
import { toast } from "sonner";
import { CATEGORY_META, currency } from "@/lib/constants";
import { Link } from "react-router-dom";
import FeatureGate from "@/components/FeatureGate";

const CATS = ["auction", "liquidation", "warehouse", "retail", "restaurant", "manufacturing", "vacant"];

export default function BrokerListingsPage() {
  return (
    <FeatureGate feature="broker_publish" title="Broker Portal is premium" blurb="Publish and manage your own direct listings for other subscribers to discover. Included in Broker, Enterprise, and Institutional.">
      <BrokerInner />
    </FeatureGate>
  );
}

function BrokerInner() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());

  function empty() {
    return { category: "auction", title: "", address: "", city: "", state: "", zip: "", latitude: "", longitude: "", price: "", building_size_sqft: "", industry: "", image_url: "" };
  }

  const load = () => api.get("/broker/listings").then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form,
      price: form.price ? parseFloat(form.price) : null,
      building_size_sqft: form.building_size_sqft ? parseInt(form.building_size_sqft) : null,
      latitude: form.latitude ? parseFloat(form.latitude) : 39.5,
      longitude: form.longitude ? parseFloat(form.longitude) : -98.35,
    };
    await api.post("/broker/listings", payload);
    setOpen(false);
    setForm(empty());
    load();
    toast.success("Listing published");
  };

  const del = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    await api.delete(`/broker/listings/${id}`);
    load();
    toast.success("Removed");
  };

  return (
    <div className="space-y-5" data-testid="broker-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Briefcase size={14} className="text-[#d4a574]" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] font-semibold">Broker Portal</div>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Your Direct Listings</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="new-listing-btn" className="flex items-center gap-2 px-4 py-2 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[13px] hover:bg-[#e8b988]"><Plus size={14} /> New Listing</button>
          </DialogTrigger>
          <DialogContent className="bg-[#1c1712] border-[#33291f] text-[#f4ede2] max-w-lg">
            <DialogHeader><DialogTitle className="font-display">Publish New Listing</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="broker-listing-form">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-[#241d16] border-[#33291f]"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{CATEGORY_META[c]?.label || c}</SelectItem>)}</SelectContent>
              </Select>
              <Input required placeholder="Title (e.g. 40,000 sqft warehouse — Phoenix)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#241d16] border-[#33291f]" />
              <Input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-[#241d16] border-[#33291f]" />
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-[#241d16] border-[#33291f]" />
                <Input placeholder="State" maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="bg-[#241d16] border-[#33291f]" />
                <Input placeholder="ZIP" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="bg-[#241d16] border-[#33291f]" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Latitude" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} className="bg-[#241d16] border-[#33291f]" />
                <Input placeholder="Longitude" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} className="bg-[#241d16] border-[#33291f]" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Price ($)" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-[#241d16] border-[#33291f]" />
                <Input placeholder="Building sqft" type="number" value={form.building_size_sqft} onChange={(e) => setForm({ ...form, building_size_sqft: e.target.value })} className="bg-[#241d16] border-[#33291f]" />
              </div>
              <Input placeholder="Image URL (optional)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-[#241d16] border-[#33291f]" />
              <button type="submit" data-testid="broker-listing-submit" className="w-full py-2 rounded bg-[#d4a574] text-[#14100c] font-semibold">Publish</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 text-[#7a6d5a] border border-dashed border-[#33291f] rounded-lg">
          <Briefcase size={24} className="mx-auto mb-3 opacity-40" />
          No listings yet. Publish your first direct listing above.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((o) => (
            <div key={o.id} className="border border-[#33291f] rounded-lg bg-[#1c1712] overflow-hidden group">
              <div className="relative aspect-[16/10]">
                <img src={o.image_url} alt="" className="w-full h-full object-cover" />
                <button onClick={() => del(o.id)} className="absolute top-2 right-2 p-1.5 rounded bg-[#14100c]/85 text-[#f87171] hover:bg-[#f87171] hover:text-[#14100c]"><Trash2 size={13} /></button>
              </div>
              <div className="p-3.5">
                <div className="font-display text-[15px] font-semibold line-clamp-1">{o.title}</div>
                <div className="text-[11px] text-[#7a6d5a] mt-1">{o.city}, {o.state}</div>
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-[#33291f]">
                  <span className="font-mono text-[13px] text-[#d4a574]">{currency(o.price)}</span>
                  <Link to={`/app/opportunities/detail/${o.id}`} className="text-[11px] text-[#d4a574] uppercase tracking-wider">View →</Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
